import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  camelCase, get, isNumber, last, size, toNumber, toString, upperFirst,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Button, OutlinedInput, Typography } from '@material-ui/core';
import messages from '../messages';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Instruction: styled(Typography)`
    margin-bottom: 20px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 700;
  `,
  Row: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    order: -1;
    margin-bottom: 1.5rem;
  `,
  Info: styled(Typography)`
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
  `,
  Number: styled.span`
    margin-right: 4px;
  `,
  Type: styled.span`
    margin-left: 4px;
  `,
  TextField: styled.div`
    display: flex;
    align-items: center;
    min-height: 36px;
    margin-bottom: 1.5rem;
  `,
  Label: styled.label`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  OutlinedInput: styled(({ classes, ...otherProps }) => (
    <OutlinedInput
      classes={{
        input: 'input',
        notchedOutline: 'notchedOutline',
      }}
      {...otherProps}
    />
  ))`
    box-sizing: border-box;
    width: 40px;
    height: 24px;
    margin-right: 4px;
    padding: 4px 8px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
    .input {
      padding: 0;
      text-align: center;
    }
    .notchedOutline {
      border-radius: 2px;
    }
  `,
  Percent: styled.span`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
  `,
  Button: styled(Button)`
    min-width: 0;
    min-height: 32px;
    padding: 3px 8px;
    :not(:first-child) {
      margin-left: 8px;
    }
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :disabled {
      color: ${({ theme }) => theme.palette.primary.contrastText};
    }
    ${({ variant, theme }) => variant === 'outlined' && css`
      color: ${theme.palette.grey[600]};
    `}
  `,
};

const AddAnswerAreaPaper = ({
  questions,
  setFieldValue,
  setStep,
  type,
}) => {
  const disabled = useMemo(
    () => !(isNumber(get('field.x', last(questions))) || isNumber(get('field.y', last(questions)))),
    [questions],
  );

  const handleChange = useCallback((event) => {
    if (toNumber(event.target.value) > 0 && toNumber(event.target.value) <= 100) {
      setFieldValue(`questions[${size(questions) - 1}].field.width`, toNumber(event.target.value));
    }

    if (toNumber(event.target.value) <= 0) {
      setFieldValue(`questions[${size(questions) - 1}].field.width`, 1);
    }

    if (toNumber(event.target.value) > 100) {
      setFieldValue(`questions[${size(questions) - 1}].field.width`, 100);
    }
  }, [questions, setFieldValue]);

  const goPrevStep = useCallback(() => {
    setStep(prevStep => prevStep - 1);
    setFieldValue(`questions[${size(questions) - 1}].field`, {});
  }, [setStep, setFieldValue, questions]);

  const goNextStep = useCallback(() => {
    setStep(prevStep => prevStep + 1);
  }, [setStep]);

  return (
    <S.Wrapper>
      <S.Instruction>
        <FormattedMessage {...messages.addAnswerAreaPaperInstruction} />
      </S.Instruction>
      <S.Row>
        <S.Info>
          <S.Number>
            <FormattedMessage
              {...messages.addAnswerAreaPaperNumber}
              values={{ number: size(questions) }}
            />
          </S.Number>
          /
          <S.Type>
            <FormattedMessage {...messages[`addAnswerAreaPaperType${upperFirst(camelCase(type))}`]} />
          </S.Type>
        </S.Info>
      </S.Row>
      <S.TextField>
        <S.Label htmlFor="answerAreaWidth">
          <FormattedMessage {...messages.addAnswerAreaPaperLabel} />
        </S.Label>
        <S.OutlinedInput
          type="number"
          inputProps={{
            min: 1,
            max: 100,
          }}
          value={toString(get('field.width', last(questions)))}
          onChange={handleChange}
        />
        <S.Percent>%</S.Percent>
      </S.TextField>
      <S.Actions>
        <S.Button type="button" variant="outlined" onClick={goPrevStep}>
          <FormattedMessage {...messages.addAnswerAreaPaperButtonPrev} />
        </S.Button>
        <S.Button
          type="button"
          color="primary"
          variant="contained"
          disabled={disabled}
          onClick={goNextStep}
        >
          <FormattedMessage {...messages.addAnswerAreaPaperButtonNext} />
        </S.Button>
      </S.Actions>
    </S.Wrapper>
  );
};

AddAnswerAreaPaper.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      options: PropTypes.arrayOf(
        PropTypes.shape({
          number: PropTypes.number.isRequired,
          content: PropTypes.string.isRequired,
          position: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
          }),
        }),
      ),
    }),
  ),
  setFieldValue: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  type: PropTypes.string,
};

AddAnswerAreaPaper.defaultProps = {
  questions: null,
  type: null,
};

export default AddAnswerAreaPaper;
